import { AuditLogDIProvider } from '@/features/audit_logs/infra/di/AuditLogDIContext';
import { DuplicateCaseDIProvider } from '@/features/duplicate_cases/infra/di/DuplicateCaseDIContext';
import { EmployeeRecordDIProvider } from '@/features/employee_record';
import { InvitationDIProvider } from '@/features/invitation';
import { PendingDhsSsaDIProvider } from '@/features/pending_dhs_ssa_cases/infra/di/PendingDhsSsaDIContext';
import { PendingEverifyDIProvider } from '@/features/pending_everify/infra/di/PendingEverifyDIContext';
import { PendingPhotoVerificationDIProvider } from '@/features/pending_photo_verification/infra/di/PendingPhotoVerificationDIContext';
import { BatchProcessingDIProvider } from '@/features/batch_processing/infra/di/BatchProcessingDIContext';
import { VisaMatrixAdminDIProvider } from '@/features/visa_matrix_admin/infra/di/VisaMatrixAdminDIContext';
import { EverifyQueueAdminDIProvider } from '@/features/everify_queue_admin/infra/di/EverifyQueueAdminDIContext';
import { StorageDIProvider } from '@/features/storage/infra/di/StorageDIContext';
import { RemediationDIProvider } from '@/features/remediation/infra/di/RemediationDIContext';
import { Action, createPermission, Resource } from '@/shared/application/types/authTypes';
import GlobalLayout from '@/shared/infra/ui/components/GlobalLayout';
import PageLoader from '@/shared/infra/ui/components/custom-ui/loader/PageLoader';
import React, { Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import { RemoteI9Route } from './RemoteI9Route';
import { EmployeeSection2Route } from './EmployeeSection2Route';

// Auth & Error — kept eager as they are first-load entrypoints
import LoginPage from '@/features/auth/infra/ui/pages/LoginPage';
import PartnerSection2Redirect from '@/features/section-2/infra/ui/pages/PartnerSection2Redirect';
import PartnerEverifyCaseRedirect from '@/features/pending_everify/infra/ui/pages/PartnerEverifyCaseRedirect';
import { useDisplacedSessionGuard } from '@/shared/infra/auth/useDisplacedSessionGuard';

// All other page-level components are lazy loaded
const DigitizedI9Screen = React.lazy(
  () => import('@/features/active_i9_queue/infra/ui/pages/DigitizedI9Screen')
);
const VisaMatrixScreen = React.lazy(
  () => import('@/features/visa_matrix_admin/infra/ui/pages/VisaMatrixScreen')
);
const DocumentMoveI9Screen = React.lazy(
  () => import('@/features/visa_matrix_admin/infra/ui/pages/DocumentMoveI9Screen')
);
const VisaMatrixInspectorScreen = React.lazy(
  () => import('@/features/visa_matrix_admin/infra/ui/pages/VisaMatrixInspectorScreen')
);
const EverifyQueueAdminScreen = React.lazy(
  () => import('@/features/everify_queue_admin/infra/ui/pages/EverifyQueueAdminScreen')
);
const ReverificationsScreen = React.lazy(
  () => import('@/features/active_i9_queue/infra/ui/pages/ReverificationsScreen')
);
const ActiveI9Screen = React.lazy(
  () => import('@/features/active_i9_queue/infra/ui/pages/ActiveI9Screen')
);
const AuditLogScreen = React.lazy(
  () => import('@/features/audit_logs/infra/ui/pages/AuditLogScreen')
);
const AuditTrailScreen = React.lazy(
  () => import('@/features/audit_logs/infra/ui/pages/AuditTrailScreen')
);
const AccountSelectionPage = React.lazy(
  () => import('@/features/auth/infra/ui/pages/AccountSelectionPage')
);
const AuthCallbackPage = React.lazy(
  () => import('@/features/auth/infra/ui/pages/AuthCallbackPage')
);
const DashboardHomePage = React.lazy(
  () => import('@/features/dashboard/infra/ui/pages/DashboardHomePage')
);
const DuplicateCaseLookupScreen = React.lazy(() =>
  import('@/features/duplicate_cases/infra/ui/pages/DuplicateCaseLookupScreen').then((m) => ({
    default: m.DuplicateCaseLookupScreen,
  }))
);
const DuplicateCaseScreen = React.lazy(() =>
  import('@/features/duplicate_cases/infra/ui/pages/DuplicateCaseScreen').then((m) => ({
    default: m.DuplicateCaseScreen,
  }))
);
const EmployeeRecordScreen = React.lazy(() =>
  import('@/features/employee_record/infra/ui/pages/EmployeeRecordScreen').then((m) => ({
    default: m.EmployeeRecordScreen,
  }))
);
const UnauthorizedPage = React.lazy(
  () => import('@/features/error/infra/ui/pages/UnauthorizedPage')
);
const BatchProcessingScreen = React.lazy(() =>
  import('@/features/batch_processing/infra/ui/pages/BatchProcessingScreen').then((m) => ({
    default: m.BatchProcessingScreen,
  }))
);
const ReverificationGapsScreen = React.lazy(() =>
  import('@/features/batch_processing/infra/ui/pages/ReverificationGapsScreen').then((m) => ({
    default: m.ReverificationGapsScreen,
  }))
);
const OpsReviewQueueScreen = React.lazy(() =>
  import('@/features/batch_processing/infra/ui/pages/OpsReviewQueueScreen').then((m) => ({
    default: m.OpsReviewQueueScreen,
  }))
);
const StorageScreen = React.lazy(() =>
  import('@/features/storage/infra/ui/pages/StorageScreen').then((m) => ({
    default: m.StorageScreen,
  }))
);
const InvitationPage = React.lazy(
  () => import('@/features/invitation/infra/ui/pages/InvitationPage')
);
const TncProcessScreen = React.lazy(
  () => import('@/features/pending_dhs_ssa_cases/features/tnc/infra/ui/pages/TncProcessScreen')
);
const PendingDhsSsaScreen = React.lazy(() =>
  import('@/features/pending_dhs_ssa_cases/infra/ui/pages/PendingDhsSsaScreen').then((m) => ({
    default: m.PendingDhsSsaScreen,
  }))
);
const EverifyCaseViewScreen = React.lazy(
  () => import('@/features/pending_everify/infra/ui/pages/EverifyCaseViewScreen')
);
const PendingEverifyScreen = React.lazy(
  () => import('@/features/pending_everify/infra/ui/pages/PendingEverifyScreen')
);
const AllEverifyScreen = React.lazy(
  () => import('@/features/pending_everify/infra/ui/pages/AllEverifyScreen')
);
const PendingPhotoVerificationScreen = React.lazy(() =>
  import('@/features/pending_photo_verification/infra/ui/pages/PendingPhotoVerificationScreen').then(
    (m) => ({ default: m.PendingPhotoVerificationScreen })
  )
);
const PhotoMatchDetailScreen = React.lazy(() =>
  import('@/features/pending_photo_verification/infra/ui/pages/PhotoMatchDetailScreen').then(
    (m) => ({ default: m.PhotoMatchDetailScreen })
  )
);
const PendingSection1Screen = React.lazy(
  () => import('@/features/pending_section1/infra/ui/pages/PendingSection1Screen')
);
const PendingSection2Screen = React.lazy(
  () => import('@/features/pending_section2/infra/ui/pages/PendingSection2Screen')
);
const PendingSsnScreen = React.lazy(
  () => import('@/features/pending_ssn/infra/ui/pages/PendingSsnScreen')
);
const SectionOne = React.lazy(() => import('@/features/section-1/infra/ui/pages/section-one'));
const RemediationQueueScreen = React.lazy(
  () => import('@/features/remediation/infra/ui/pages/RemediationQueueScreen')
);
const RemediationRecordScreen = React.lazy(
  () => import('@/features/remediation/infra/ui/pages/RemediationRecordScreen')
);
const EmployeeRemediationScreen = React.lazy(
  () => import('@/features/remediation/infra/ui/pages/EmployeeRemediationScreen')
);
const Section2Wizard = React.lazy(
  () => import('@/features/section-2/infra/ui/pages/Section2Wizard')
);
const SupplementalWizard = React.lazy(
  () => import('@/features/supplement/infra/ui/pages/SupplementWizard')
);
const RemoteI9AuthPage = React.lazy(
  () => import('@/features/remote_i9/infra/ui/pages/RemoteI9AuthPage')
);
const RemoteI9LandingPage = React.lazy(
  () => import('@/features/remote_i9/infra/ui/pages/RemoteI9LandingPage')
);
const RemoteI9SuccessPage = React.lazy(
  () => import('@/features/remote_i9/infra/ui/pages/RemoteI9SuccessPage')
);
const EmployeeSection2AuthPage = React.lazy(
  () => import('@/features/employee_section2/infra/ui/pages/EmployeeSection2AuthPage')
);
const EmployeeSection2LandingPage = React.lazy(
  () => import('@/features/employee_section2/infra/ui/pages/EmployeeSection2LandingPage')
);
const EmployeeSection2SuccessPage = React.lazy(
  () => import('@/features/employee_section2/infra/ui/pages/EmployeeSection2SuccessPage')
);

/** Needs to sit inside the router to navigate; renders nothing. */
const DisplacedSessionGuard: React.FC = () => {
  useDisplacedSessionGuard();
  return null;
};

export const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      {/* Signs this tab out if another tab signs in as someone else — one cookie per browser means
          the newest sign-in takes the session from every tab, and a displaced tab would otherwise
          keep showing the old user while acting as the new one. Inert when not signed in, so the
          public candidate screens are never affected. */}
      <DisplacedSessionGuard />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/granttoken" element={<AuthCallbackPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />

          {/* Public Employee I9 Form (no authentication required) */}
          <Route path="/sectionOne/:encryptedI9formId" element={<SectionOne />} />
          <Route path="/sectionOne" element={<SectionOne />} />

          {/* Partner Section 2 deep link (VTK-1918). Public only in the sense that it resolves the
              link — it forwards to /section-2/:i9FormId, which keeps its own auth + permission
              guard, so the employer still has to be logged in and entitled. */}
          <Route path="/sectiontwo" element={<PartnerSection2Redirect />} />

          {/* Partner E-Verify case deep link — the employer's other partner-minted link, resolved
              the same way. Forwards to /e-verify/case/:everifyCaseQueueId, which keeps its own
              eVerify.read guard, so the token only names the case; the session authorizes it. */}
          <Route path="/everifycase" element={<PartnerEverifyCaseRedirect />} />

          {/* Public employee Section 1 remediation (capability-token gated, VTK-1835) */}
          <Route
            path="/remediation/section1"
            element={
              <RemediationDIProvider>
                <EmployeeRemediationScreen />
              </RemediationDIProvider>
            }
          />

          <Route element={<RemoteI9Route />}>
            <Route path="/remote-i9/auth/:token" element={<RemoteI9AuthPage />} />
            <Route path="/remote-i9/landing" element={<RemoteI9LandingPage />} />
            <Route path="/remote-i9/success" element={<RemoteI9SuccessPage />} />
          </Route>

          <Route element={<EmployeeSection2Route />}>
            <Route
              path="/employee-filled-section2/auth/:token"
              element={<EmployeeSection2AuthPage />}
            />
            <Route
              path="/employee-filled-section2/landing"
              element={<EmployeeSection2LandingPage />}
            />
            <Route
              path="/employee-filled-section2/success"
              element={<EmployeeSection2SuccessPage />}
            />
          </Route>

          {/* Protected Routes - Require Authentication and Permissions */}
          <Route element={<GlobalLayout />}>
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.DASHBOARD, Action.READ)}
                >
                  <DashboardHomePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account-selection"
              element={
                <ProtectedRoute>
                  <AccountSelectionPage />
                </ProtectedRoute>
              }
            />
            {/* ── Historical I-9 Storage (VTK-1732) ────────────────────── */}
            {/* Client-facing + internal: gated on the formI9Storage permission (super-admins hold
                it via their role; client users only via a per-user custom permission set assigned
                in VerifyToday — VTK-1878 interim model, paid perms are never client role defaults). */}
            <Route
              path="/storage"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.FORM_I9_STORAGE, Action.READ)}
                >
                  <StorageDIProvider>
                    <StorageScreen />
                  </StorageDIProvider>
                </ProtectedRoute>
              }
            />
            {/* ── Digitization ─────────────────────────────────────────── */}
            {/* Client-facing digitized-I-9 list (VTK-1740) — auth only, NOT permission-gated.
                The paid permission protects the ACT of digitizing (/digitization/batch and the
                digitization/batch/* API), not reading the resulting records: this screen is the
                I-9 Cases table filtered to DIGITIZED, so it serves the same i9_form data the
                user can already see at /active-i9-queue. */}
            <Route
              path="/digitization"
              element={
                <ProtectedRoute>
                  <DigitizedI9Screen />
                </ProtectedRoute>
              }
            />
            {/* Its reads are only .read-gated, but the workspace exists solely to AUTHOR digitized
                records, so entry is deliberately gated on .update. Only a super admin can reach it
                and a super admin is never read-only, so this costs no one their view access — the
                records themselves stay viewable at /digitization and /employee-record. */}
            {/* The vendor import is its own URL so browser back leaves it, rather than skipping the
                whole screen. Same screen and same guards — `:batchId` would otherwise swallow
                `import` and try to open a batch by that id. */}
            <Route
              path="/digitization/batch/import"
              element={
                <ProtectedRoute
                  requireSuperAdmin
                  requiredPermission={createPermission(
                    Resource.FORM_I9_DIGITIZATION,
                    Action.UPDATE
                  )}
                >
                  <BatchProcessingDIProvider>
                    <BatchProcessingScreen />
                  </BatchProcessingDIProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/digitization/batch/:batchId?/bundle?/:bundleId?"
              element={
                <ProtectedRoute
                  requireSuperAdmin
                  requiredPermission={createPermission(
                    Resource.FORM_I9_DIGITIZATION,
                    Action.UPDATE
                  )}
                >
                  <BatchProcessingDIProvider>
                    <BatchProcessingScreen />
                  </BatchProcessingDIProvider>
                </ProtectedRoute>
              }
            />
            {/* The Visa Matrix (VTK-2063, VTK-2064): internal, super admins only. One page whose four
                tabs are these four routes, so a deep link opens on its tab and the browser's back
                button steps between tabs. Every path is also in the sidebar's VISA_MATRIX_ROUTES, and
                the sidebar links the first one only. */}
            <Route path="/visa-matrix" element={<Navigate to="/visa-matrix/rules" replace />} />
            <Route
              path="/visa-matrix/rules"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <VisaMatrixAdminDIProvider>
                    <VisaMatrixScreen tab="rules" />
                  </VisaMatrixAdminDIProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/visa-matrix/compare"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <VisaMatrixAdminDIProvider>
                    <VisaMatrixScreen tab="compare" />
                  </VisaMatrixAdminDIProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/visa-matrix/extension-rules"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <VisaMatrixAdminDIProvider>
                    <VisaMatrixScreen tab="extension-rules" />
                  </VisaMatrixAdminDIProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/visa-matrix/outcomes"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <VisaMatrixAdminDIProvider>
                    <VisaMatrixScreen tab="outcomes" />
                  </VisaMatrixAdminDIProvider>
                </ProtectedRoute>
              }
            />
            {/* VTK-2089: the staff queue for moving existing I-9s into the new document format, a tab
                of the Visa Matrix page, and one I-9 of it, reached from the queue. */}
            <Route
              path="/visa-matrix/document-move"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <VisaMatrixAdminDIProvider>
                    <VisaMatrixScreen tab="document-move" />
                  </VisaMatrixAdminDIProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/visa-matrix/move-runs"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <VisaMatrixAdminDIProvider>
                    <VisaMatrixScreen tab="move-runs" />
                  </VisaMatrixAdminDIProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/visa-matrix/document-move/:i9FormId"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <VisaMatrixAdminDIProvider>
                    <DocumentMoveI9Screen />
                  </VisaMatrixAdminDIProvider>
                </ProtectedRoute>
              }
            />
            {/* The I-9 Inspector is reached from an I-9 ("Matrix decisions" on the employee record,
                the E-Verify case screen and the row menus), never from the sidebar or the tabs. */}
            <Route
              path="/visa-matrix/inspector"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <VisaMatrixAdminDIProvider>
                    <VisaMatrixInspectorScreen />
                  </VisaMatrixAdminDIProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/visa-matrix/inspector/:i9FormId"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <VisaMatrixAdminDIProvider>
                    <VisaMatrixInspectorScreen />
                  </VisaMatrixAdminDIProvider>
                </ProtectedRoute>
              }
            />
            {/* Every account's stuck E-Verify case creations, internal only. The role is the real
                gate (the API checks it alone); the permission keeps the E-Verify route rule whole.
                Mirrored in the sidebar's SUPER_ADMIN_ROUTES and ROUTE_PERMISSIONS. */}
            <Route
              path="/admin/e-verify-queue"
              element={
                <ProtectedRoute
                  requireSuperAdmin
                  requiredPermission={createPermission(Resource.EVERIFY, Action.READ)}
                >
                  <EverifyQueueAdminDIProvider>
                    <EverifyQueueAdminScreen />
                  </EverifyQueueAdminDIProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/digitization/ops-review"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <BatchProcessingDIProvider>
                    <OpsReviewQueueScreen />
                  </BatchProcessingDIProvider>
                </ProtectedRoute>
              }
            />
            {/* Digitized records whose reverification status cannot be worked out (VTK-1950).
                Super-admin only: an internal data-quality view over records our own staff
                transcribed, not something a client is shown. Gated on the digitization read
                permission as well, because the endpoint behind it is — without both, a super admin
                would reach the screen and meet nothing but its error state. */}
            <Route
              path="/digitization/reverification-gaps"
              element={
                <ProtectedRoute
                  requireSuperAdmin
                  requiredPermission={createPermission(Resource.FORM_I9_DIGITIZATION, Action.READ)}
                >
                  <BatchProcessingDIProvider>
                    <ReverificationGapsScreen />
                  </BatchProcessingDIProvider>
                </ProtectedRoute>
              }
            />
            {/* ── Remediation (VTK-1743/1865) ──────────────────────────── */}
            <Route
              path="/remediation"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.FORM_I9_REMEDIATION, Action.READ)}
                >
                  <RemediationDIProvider>
                    <RemediationQueueScreen />
                  </RemediationDIProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/remediation/records/:id"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.FORM_I9_REMEDIATION, Action.READ)}
                >
                  <RemediationDIProvider>
                    <RemediationRecordScreen />
                  </RemediationDIProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pending-section-2"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.SECTION_2, Action.READ)}
                >
                  <PendingSection2Screen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/section-2/:i9FormId"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.SECTION_2, Action.UPDATE)}
                >
                  <Section2Wizard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/supplement-b/:i9FormId"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.SUPPLEMENT_B, Action.UPDATE)}
                >
                  <SupplementalWizard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pending-section-1"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.SECTION_1, Action.READ)}
                >
                  <PendingSection1Screen />
                </ProtectedRoute>
              }
            />
            {/* Read to enter: the queue is a view of pending records, and each write on it
                (submit SSN, request SSN, archive) is gated on its own permission at the control. */}
            <Route
              path="/pending-ssn"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.SSN_ONLY_SECTION_1, Action.READ)}
                >
                  <PendingSsnScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pending-photo-verification"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.PHOTO_MATCHING, Action.READ)}
                >
                  <PendingPhotoVerificationDIProvider>
                    <PendingPhotoVerificationScreen />
                  </PendingPhotoVerificationDIProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/e-verify/photo-match/:everifyCaseId"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.PHOTO_MATCHING, Action.READ)}
                >
                  <PendingPhotoVerificationDIProvider>
                    <PhotoMatchDetailScreen />
                  </PendingPhotoVerificationDIProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pending-dhs-ssa-cases"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.DHS_SSA_COMMUNICATION, Action.READ)}
                >
                  <PendingDhsSsaDIProvider>
                    <PendingDhsSsaScreen />
                  </PendingDhsSsaDIProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="e-verify/tnc/:caseId"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.EVERIFY, Action.UPDATE)}
                >
                  <TncProcessScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pending-everify-cases"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.EVERIFY, Action.READ)}
                >
                  <PendingEverifyDIProvider>
                    <PendingEverifyScreen />
                  </PendingEverifyDIProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/all-everify-cases"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.EVERIFY, Action.READ)}
                >
                  <PendingEverifyDIProvider>
                    <AllEverifyScreen />
                  </PendingEverifyDIProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/e-verify/case/:everifyCaseQueueId"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.EVERIFY, Action.READ)}
                >
                  <PendingEverifyDIProvider>
                    <EverifyCaseViewScreen />
                  </PendingEverifyDIProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pending-everify-cases/e-verify/view/:caseId"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.EVERIFY, Action.READ)}
                >
                  <PendingEverifyDIProvider>
                    <EverifyCaseViewScreen />
                  </PendingEverifyDIProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/audit-logs/:i9FormId"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.DASHBOARD, Action.READ)}
                >
                  <AuditLogDIProvider>
                    <AuditLogScreen />
                  </AuditLogDIProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/audit-trail/:i9FormId"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.SECTION_1, Action.READ)}
                >
                  <AuditLogDIProvider>
                    <AuditTrailScreen />
                  </AuditLogDIProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employees/invite"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.SECTION_1, Action.READ)}
                >
                  <InvitationDIProvider>
                    <InvitationPage />
                  </InvitationDIProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee-record/:i9FormId"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.DASHBOARD, Action.READ)}
                >
                  <EmployeeRecordDIProvider>
                    <EmployeeRecordScreen />
                  </EmployeeRecordDIProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/active-i9-queue"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.DASHBOARD, Action.READ)}
                >
                  <ActiveI9Screen />
                </ProtectedRoute>
              }
            />
            {/* The Reverifications dashboard tile links here (VTK-1950). Same table, same data and
                the same dashboard.read gate as /active-i9-queue — it is that list with the queue's
                own Supplement B action applied, not a new surface, so it grants nobody anything
                they cannot already see. */}
            <Route
              path="/reverifications-due"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.DASHBOARD, Action.READ)}
                >
                  <ReverificationsScreen />
                </ProtectedRoute>
              }
            />
            {/* Duplicate E-Verify cases (VTK-1857) — eVerify.read, not dashboard.read. These are
                E-Verify case-management screens, and the Duplicate Cases dashboard tile has always
                keyed off eVerify.read, so dashboard.read here left that tile's gate bypassable by
                URL. Every role that exists today holds eVerify.read. */}
            <Route
              path="e-verify/duplicate-cases"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.EVERIFY, Action.READ)}
                >
                  <DuplicateCaseDIProvider>
                    <DuplicateCaseScreen />
                  </DuplicateCaseDIProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="e-verify/duplicate-cases/:everifyCaseDuplicateId"
              element={
                <ProtectedRoute
                  requiredPermission={createPermission(Resource.EVERIFY, Action.READ)}
                >
                  <DuplicateCaseDIProvider>
                    <DuplicateCaseLookupScreen />
                  </DuplicateCaseDIProvider>
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Default Redirects */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* Catch-all route for unmatched paths */}
          <Route path="*" element={<Navigate to="/unauthorized" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};
